'use client';
import { useResource, fetchWithCache } from '@/lib/use-resource';
import type { ScoredStock } from '@/lib/compute';
import type { BacktestOutput } from '@/lib/backtest';

// Single hook that owns all the data the main visualizer page needs.
// Extracted from app/page.tsx — used to be six separate useEffect/fetch
// blocks that flickered the page through six independent loading states.
// Now: one hook, useResource cache + dedup, one cohesive loading story.

export interface TechnicalStock {
  symbol: string;
  technicalScore: number;
  rsi?: number | null;
  rsiPrev?: number | null;
  stochRsi?: number;
  macdHistogram?: number;
  macdLine?: number;
  signalLine?: number;
}

interface TechnicalResponse {
  updatedAt: string;
  stocks: TechnicalStock[];
}

interface ApiResponse {
  updatedAt: string;
  count: number;
  stocks: ScoredStock[];
  error?: string;
}

interface DoubleSignalsResponse {
  doubleOversold?: { symbol: string }[];
  doubleOverbought?: { symbol: string }[];
}

interface NewOpportunitiesResponse {
  enteredAlpha?: string[];
  exitedAlpha?: string[];
}

export interface VisualizerData {
  data: ApiResponse | null;
  error: string | null;
  backtestData: BacktestOutput | null;
  technicalData: TechnicalStock[];
  technicalWeeklyData: TechnicalStock[];
  technicalMonthlyData: TechnicalStock[];
  doubleSignals: { doubleOversold: string[]; doubleOverbought: string[] };
  enteredAlphaSymbols: Set<string>;
  exitedAlphaSymbols: Set<string>;
  loadHistorical: (period: string | null) => Promise<{ stocks: ScoredStock[] | null; date: string | null }>;
}

export function useVisualizerData(): VisualizerData {
  const { data: stocksData, error: stocksError } = useResource<ApiResponse>('/api/stocks');
  const { data: backtestData } = useResource<BacktestOutput>('/api/backtest');
  const { data: techDaily } = useResource<TechnicalResponse>('/api/technical');
  const { data: techWeekly } = useResource<TechnicalResponse>('/api/technical-weekly');
  const { data: techMonthly } = useResource<TechnicalResponse>('/api/technical-monthly');
  const { data: doubleRaw } = useResource<DoubleSignalsResponse>('/api/double-signals');
  const { data: newOpps } = useResource<NewOpportunitiesResponse>('/api/new-opportunities');

  // Derive at render time — no setState-in-effect needed.
  const error = stocksError ?? stocksData?.error ?? null;

  const doubleSignals = {
    doubleOversold: (doubleRaw?.doubleOversold ?? []).map((s) => s.symbol),
    doubleOverbought: (doubleRaw?.doubleOverbought ?? []).map((s) => s.symbol),
  };

  // Historical lookup is on-demand (not on mount), so it stays as a
  // method rather than a useResource. Caching still applies through
  // fetchWithCache.
  const loadHistorical = async (period: string | null) => {
    if (!period) return { stocks: null, date: null };

    // YYYY-MM → fetch from /api/full-monthly (new path). Mock partial ScoredStock
    // shapes from the sparse cache (factor data isn't stored historically; the
    // home-page scatter only needs Q/V/Z + sector/marketCap to render).
    const monthMatch = /^(\d{4})-(\d{2})$/.exec(period);
    if (monthMatch) {
      try {
        const d = await fetchWithCache<{ stocks?: Array<{ symbol: string; sector: string; industry: string; marketCap: number; price: number; qualityScore: number; valuationScore: number; composite: number; priceMomentumZ?: number }> }>(
          `/api/full-monthly?month=${period}`,
        );
        if (d.stocks?.length) {
          const stocks: ScoredStock[] = d.stocks.map((s) => ({
            symbol: s.symbol,
            name: s.symbol,
            sector: s.sector,
            industry: s.industry,
            marketCap: s.marketCap,
            price: s.price,
            factors: {} as ScoredStock['factors'],
            pillarScores: { profitability: null, growth: null, safety: null, shareholderReturns: null, momentum: null } as ScoredStock['pillarScores'],
            qualityScore: s.qualityScore,
            valuationScore: s.valuationScore,
            // Z-axis = 12-1 price momentum (z-scored within the historical vintage).
            // Replaces zero-fill so historical view doesn't pancake all stocks at Z=0.
            momentumScore: s.priceMomentumZ ?? 0,
          }));
          return { stocks, date: period };
        }
      } catch {
        /* swallow */
      }
      return { stocks: null, date: null };
    }

    // Legacy period-tab path (kept for backwards compat with /api/historical)
    try {
      const d = await fetchWithCache<{ available?: boolean; stocks?: ScoredStock[]; date?: string }>(
        `/api/historical?period=${period}`,
      );
      if (d.available && d.stocks?.length) {
        return { stocks: d.stocks, date: d.date ?? null };
      }
    } catch {
      /* swallow — caller treats null as "no data" */
    }
    return { stocks: null, date: null };
  };

  return {
    data: stocksData ?? null,
    error,
    backtestData: backtestData ?? null,
    technicalData: techDaily?.stocks ?? [],
    technicalWeeklyData: techWeekly?.stocks ?? [],
    technicalMonthlyData: techMonthly?.stocks ?? [],
    doubleSignals,
    enteredAlphaSymbols: new Set(newOpps?.enteredAlpha ?? []),
    exitedAlphaSymbols: new Set(newOpps?.exitedAlpha ?? []),
    loadHistorical,
  };
}
